import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UserPlus, Search, Clock, CheckCircle, AlertTriangle, XCircle,
  ChevronRight, RefreshCw, Play, Settings, User, Briefcase,
  Monitor, Phone, Shield, Mail, Loader2, Plus, X, Trash2, RotateCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import api from '../lib/api';

const STATUS_CONFIG = {
  'In Progress': { color: 'bg-blue-100 text-blue-700', icon: Clock },
  'Processing': { color: 'bg-blue-100 text-blue-700', icon: Clock },
  'Completed': { color: 'bg-green-100 text-green-700', icon: CheckCircle },
  'Closed': { color: 'bg-green-100 text-green-700', icon: CheckCircle },
  'Needs Review': { color: 'bg-yellow-100 text-yellow-700', icon: AlertTriangle },
  'Failed': { color: 'bg-red-100 text-red-700', icon: XCircle },
  'Open': { color: 'bg-slate-100 text-slate-700', icon: Clock },
};

const STEP_ICONS = {
  'create_user': User,
  'assign_groups': Shield,
  'assign_licenses': Shield,
  'mirror_permissions': Shield,
  'business_central': Briefcase,
  'laptop_assignment': Monitor,
  'teams_phone': Phone,
  'sharepoint_onegamer': Mail,
  'send_notifications': Mail,
};

function StepBadge({ step, isCompleted, isFailed }) {
  const Icon = STEP_ICONS[step] || CheckCircle;
  const label = step.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  
  if (isCompleted) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-50 text-green-700 border border-green-200">
        <CheckCircle className="h-3 w-3" />
        {label}
      </span>
    );
  }
  if (isFailed) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-50 text-red-700 border border-red-200">
        <XCircle className="h-3 w-3" />
        {label}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-slate-50 text-slate-500 border border-slate-200">
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [polling, setPolling] = useState(false);
  const [config, setConfig] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('tickets'); // 'tickets' or 'phone-pool'
  const [phonePool, setPhonePool] = useState({ numbers: [], total: 0, available: 0, assigned: 0 });
  const [phoneInput, setPhoneInput] = useState('');
  const [addingNumbers, setAddingNumbers] = useState(false);
  const [rerunningTicket, setRerunningTicket] = useState(null);

  const DEPARTMENTS = [
    'Finance', 'Accounting', 'Sales', 'Inside Sales', 'OSR', 'KAM',
    'Operations', 'Logistics', 'Supply Chain', 'Warehouse', 'Quality',
    'IT', 'HR', 'Marketing', 'Admin',
    'Emerging Markets', 'Beverage', 'Consumer', 'NBD', 'Procurement',
  ];

  const emptyForm = {
    full_name: '', email_alias: '', job_title: '', department: '',
    office_location: '', manager: '', start_date: '', cell_phone: '',
    personal_email: '', bc: false, bc_role: '', spiro: false,
    teams_phone: true, business_cards: false, website: false,
    jd_edwards: false, laptop_model: '',
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    fetchTickets();
    fetchConfig();
    fetchPhonePool();
  }, []);

  const fetchTickets = async () => {
    try {
      const res = await api.getOnboardingTickets(50);
      setTickets(res.data.tickets || []);
    } catch (err) {
      console.error('Failed to fetch onboarding tickets:', err);
      toast.error('Failed to load onboarding tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const res = await api.getOnboardingConfig();
      setConfig(res.data);
    } catch (err) {
      console.error('Failed to fetch config:', err);
    }
  };

  const fetchPhonePool = async () => {
    try {
      const res = await api.getPhonePool();
      setPhonePool(res.data);
    } catch (err) {
      console.error('Failed to fetch phone pool:', err);
    }
  };

  const handleAddNumbers = async () => {
    const numbers = phoneInput.split('\n').map(n => n.trim()).filter(Boolean);
    if (numbers.length === 0) { toast.error('Enter at least one phone number'); return; }
    setAddingNumbers(true);
    try {
      const res = await api.addPhoneNumbers(numbers);
      toast.success(`Added ${res.data.added} number(s)${res.data.duplicates ? `, ${res.data.duplicates} duplicate(s) skipped` : ''}`);
      setPhoneInput('');
      fetchPhonePool();
    } catch (err) {
      toast.error('Failed to add numbers: ' + (err.response?.data?.detail || err.message));
    } finally {
      setAddingNumbers(false);
    }
  };

  const handleReleaseNumber = async (number) => {
    try {
      await api.releasePhoneNumber(number);
      toast.success(`Released ${number}`);
      fetchPhonePool();
    } catch (err) {
      toast.error('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleRemoveNumber = async (number) => {
    if (!window.confirm(`Remove ${number} from the pool entirely?`)) return;
    try {
      await api.removePhoneNumber(number);
      toast.success(`Removed ${number}`);
      fetchPhonePool();
    } catch (err) {
      toast.error('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleRerunOnboarding = async (e, ticketId, failedSteps) => {
    e.stopPropagation(); // Don't navigate to ticket detail
    setRerunningTicket(ticketId);
    try {
      const stepNames = failedSteps.map(s => s.step || s);
      const res = await api.rerunOnboarding(ticketId, stepNames);
      const data = res.data;
      if (data.succeeded?.length > 0) {
        toast.success(`Retry succeeded for: ${data.succeeded.join(', ')}`);
      }
      if (data.still_failed?.length > 0) {
        const failNames = data.still_failed.map(f => f.step).join(', ');
        toast.warning(`Still failing: ${failNames}`);
      }
      if (data.succeeded?.length === 0 && data.still_failed?.length === 0) {
        toast.info(data.message || 'No steps to retry');
      }
      fetchTickets();
    } catch (err) {
      toast.error('Retry failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRerunningTicket(null);
    }
  };

  const handlePollNow = async () => {
    setPolling(true);
    try {
      const res = await api.triggerOnboardingPoll();
      const data = res.data;
      if (data.results && data.results.length > 0) {
        toast.success(`Processed ${data.results.length} new hire request(s)!`);
        fetchTickets();
      } else {
        toast.info('No new hire emails found');
      }
    } catch (err) {
      toast.error('Poll failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setPolling(false);
    }
  };

  const handleSubmitNewHire = async () => {
    if (!form.full_name.trim() || !form.email_alias.trim()) {
      toast.error('Full name and email alias are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      // Clean up empty optional fields
      Object.keys(payload).forEach(k => {
        if (payload[k] === '' || payload[k] === null) delete payload[k];
      });
      const res = await api.processOnboarding(payload);
      toast.success(`Onboarding started for ${form.full_name}! Ticket #${res.data.ticket_number}`);
      setShowForm(false);
      setForm(emptyForm);
      fetchTickets();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error('Onboarding failed: ' + detail);
    } finally {
      setSubmitting(false);
    }
  };

  const filtered = tickets.filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (t.summary || '').toLowerCase().includes(q) ||
      (t.onboarding_data?.request?.full_name || '').toLowerCase().includes(q) ||
      (t.onboarding_data?.request?.department || '').toLowerCase().includes(q) ||
      (t.onboarding_data?.request?.job_title || '').toLowerCase().includes(q)
    );
  });

  // Stats
  const completed = tickets.filter(t => t.status === 'Closed' || t.status === 'Completed').length;
  const inProgress = tickets.filter(t => t.status === 'In Progress' || t.status === 'Processing').length;
  const failed = tickets.filter(t => t.status === 'Needs Review' || t.status === 'Failed').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="onboarding-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800" data-testid="onboarding-title">New Hire Onboarding</h1>
          <p className="text-sm text-slate-500 mt-1">
            Automated onboarding via email trigger
            {config?.is_test_mode && (
              <Badge className="ml-2 bg-amber-100 text-amber-700 border-amber-300">Test Mode</Badge>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" size="sm"
            onClick={handlePollNow} 
            disabled={polling}
            data-testid="poll-now-btn"
          >
            {polling ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            {polling ? 'Polling...' : 'Poll Now'}
          </Button>
          <Button 
            size="sm"
            onClick={() => setShowForm(true)}
            data-testid="new-hire-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            New Hire
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Total</p>
                <p className="text-2xl font-bold text-slate-800" data-testid="stat-total">{tickets.length}</p>
              </div>
              <UserPlus className="h-8 w-8 text-slate-300" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Completed</p>
                <p className="text-2xl font-bold text-green-600" data-testid="stat-completed">{completed}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">In Progress</p>
                <p className="text-2xl font-bold text-blue-600" data-testid="stat-progress">{inProgress}</p>
              </div>
              <Clock className="h-8 w-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Needs Review</p>
                <p className="text-2xl font-bold text-amber-600" data-testid="stat-failed">{failed}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-amber-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tab Switcher */}
      <div className="flex gap-1 border-b">
        <button
          onClick={() => setActiveTab('tickets')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'tickets' ? 'border-slate-800 text-slate-800' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
          data-testid="tab-tickets"
        >
          Onboarding Tickets
        </button>
        <button
          onClick={() => setActiveTab('phone-pool')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${activeTab === 'phone-pool' ? 'border-slate-800 text-slate-800' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
          data-testid="tab-phone-pool"
        >
          <Phone className="h-3.5 w-3.5" />
          Phone Pool
          {phonePool.available > 0 && (
            <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">{phonePool.available}</span>
          )}
        </button>
      </div>

      {/* Phone Pool Tab */}
      {activeTab === 'phone-pool' && (
        <div className="space-y-4">
          {/* Pool Stats */}
          <div className="grid grid-cols-3 gap-4">
            <Card><CardContent className="pt-4 pb-3">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Total Numbers</p>
              <p className="text-2xl font-bold text-slate-800" data-testid="pool-total">{phonePool.total}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4 pb-3">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Available</p>
              <p className="text-2xl font-bold text-green-600" data-testid="pool-available">{phonePool.available}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4 pb-3">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Assigned</p>
              <p className="text-2xl font-bold text-blue-600" data-testid="pool-assigned">{phonePool.assigned}</p>
            </CardContent></Card>
          </div>

          {/* Add Numbers */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Add Phone Numbers</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-slate-500 mb-2">One number per line (e.g. +1 612 555 0100)</p>
              <textarea
                className="w-full border rounded px-3 py-2 text-sm font-mono h-24 resize-none"
                placeholder={"+1 612 555 0100\n+1 612 555 0101\n+1 612 555 0102"}
                value={phoneInput}
                onChange={e => setPhoneInput(e.target.value)}
                data-testid="phone-input"
              />
              <Button size="sm" onClick={handleAddNumbers} disabled={addingNumbers} className="mt-2" data-testid="add-numbers-btn">
                {addingNumbers ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
                Add to Pool
              </Button>
            </CardContent>
          </Card>

          {/* Number List */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Phone Numbers</CardTitle>
            </CardHeader>
            <CardContent>
              {phonePool.numbers.length === 0 ? (
                <p className="text-sm text-slate-400 py-4 text-center">No phone numbers in pool. Add some above.</p>
              ) : (
                <div>
                  {/* Summary by priority */}
                  <div className="flex gap-3 mb-3 text-xs">
                    <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full" data-testid="pool-p1-count">
                      P1 ({phonePool.numbers.filter(n => n.priority === 1 && n.status === 'available').length} avail)
                    </span>
                    <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full" data-testid="pool-p2-count">
                      P2 ({phonePool.numbers.filter(n => n.priority === 2 && n.status === 'available').length} avail)
                    </span>
                    <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full" data-testid="pool-p3-count">
                      P3 ({phonePool.numbers.filter(n => n.priority === 3 && n.status === 'available').length} avail)
                    </span>
                  </div>
                  <div className="divide-y max-h-[500px] overflow-y-auto">
                    {phonePool.numbers.map(n => (
                      <div key={n.number} className="flex items-center justify-between py-2 px-1">
                        <div className="flex items-center gap-3">
                          {n.priority && (
                            <span className={`text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                              n.priority === 1 ? 'bg-emerald-100 text-emerald-700' : 
                              n.priority === 2 ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'
                            }`}>{n.priority}</span>
                          )}
                          <span className="font-mono text-sm">{n.number}</span>
                          <Badge className={
                            n.status === 'available' ? 'bg-green-50 text-green-700' : 
                            n.status === 'voice_app' || n.status === 'conference' ? 'bg-purple-50 text-purple-700' :
                            'bg-blue-50 text-blue-700'
                          }>
                            {n.status}
                          </Badge>
                          {n.assigned_to && <span className="text-xs text-slate-500">{n.assigned_to}</span>}
                          {n.city && <span className="text-[10px] text-slate-400">{n.city}</span>}
                        </div>
                        <div className="flex items-center gap-1">
                          {n.status === 'assigned' && (
                            <Button variant="ghost" size="sm" onClick={() => handleReleaseNumber(n.number)} className="text-xs h-7 text-amber-600 hover:text-amber-700">
                              Release
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleRemoveNumber(n.number)} className="text-xs h-7 text-red-500 hover:text-red-600">
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tickets Tab */}
      {activeTab === 'tickets' && <>
      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Search by name, department, or title..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid="onboarding-search"
        />
      </div>

      {/* Ticket List */}
      <div className="space-y-2">
        {filtered.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <UserPlus className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No onboarding tickets found</p>
              <p className="text-sm text-slate-400 mt-1">New hire emails will appear here when processed</p>
            </CardContent>
          </Card>
        ) : (
          filtered.map((ticket) => {
            const req = ticket.onboarding_data?.request || {};
            const stepsCompleted = ticket.onboarding_data?.steps_completed || [];
            const stepsFailed = (ticket.onboarding_data?.steps_failed || []).map(s => s.step || s);
            const statusCfg = STATUS_CONFIG[ticket.status] || STATUS_CONFIG['Open'];
            const StatusIcon = statusCfg.icon;
            const isTest = ticket.onboarding_data?.is_test_mode;

            return (
              <Card 
                key={ticket.ticket_id} 
                className="hover:shadow-md transition-shadow cursor-pointer border-l-4"
                style={{ borderLeftColor: ticket.status === 'Needs Review' || ticket.status === 'Failed' ? '#ef4444' : stepsCompleted.length > 0 ? '#22c55e' : '#3b82f6' }}
                onClick={() => navigate(`/tickets/managed/${ticket.ticket_id}`)}
                data-testid={`onboarding-ticket-${ticket.ticket_number}`}
              >
                <CardContent className="py-3 px-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-slate-400 font-mono">#{ticket.ticket_number}</span>
                        <Badge className={statusCfg.color}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {ticket.status}
                        </Badge>
                        {isTest && <Badge className="bg-amber-50 text-amber-600 border-amber-200 text-[10px]">TEST</Badge>}
                      </div>
                      
                      <h3 className="font-semibold text-slate-800 truncate">{req.full_name || ticket.summary}</h3>
                      
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                        {req.job_title && <span>{req.job_title}</span>}
                        {req.department && <span className="text-slate-400">|</span>}
                        {req.department && <span>{req.department}</span>}
                        {req.manager && <span className="text-slate-400">|</span>}
                        {req.manager && <span>Mgr: {req.manager}</span>}
                      </div>

                      {/* Steps progress */}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {stepsCompleted.map(s => <StepBadge key={s} step={s} isCompleted />)}
                        {stepsFailed.map(s => <StepBadge key={s} step={s} isFailed />)}
                      </div>

                      {/* Retry button for tickets with failed steps */}
                      {stepsFailed.length > 0 && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="mt-2 text-xs h-7 text-amber-700 border-amber-300 hover:bg-amber-50"
                          onClick={(e) => handleRerunOnboarding(e, ticket.ticket_id, ticket.onboarding_data?.steps_failed || [])}
                          disabled={rerunningTicket === ticket.ticket_id}
                          data-testid={`retry-btn-${ticket.ticket_number}`}
                        >
                          {rerunningTicket === ticket.ticket_id ? (
                            <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          ) : (
                            <RotateCw className="h-3 w-3 mr-1" />
                          )}
                          {rerunningTicket === ticket.ticket_id ? 'Retrying...' : `Retry ${stepsFailed.length} Failed Step${stepsFailed.length > 1 ? 's' : ''}`}
                        </Button>
                      )}
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      <div className="text-right text-xs text-slate-400">
                        {ticket.created_on && new Date(ticket.created_on).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                      <ChevronRight className="h-4 w-4 text-slate-300" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
      </>}

      {/* New Hire Form Dialog */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 pt-12 overflow-y-auto" data-testid="new-hire-dialog">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 mb-12">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-slate-800">New Hire Onboarding</h2>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Employee Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Full Name *</Label>
                  <Input value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} placeholder="Jane Doe" data-testid="form-full-name" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Email Alias *</Label>
                  <div className="flex items-center gap-1">
                    <Input value={form.email_alias} onChange={e => setForm({...form, email_alias: e.target.value})} placeholder="jdoe" data-testid="form-email-alias" />
                    <span className="text-xs text-slate-400 whitespace-nowrap">@gamerpackaging.com</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Job Title</Label>
                  <Input value={form.job_title} onChange={e => setForm({...form, job_title: e.target.value})} placeholder="Sales Representative" data-testid="form-job-title" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Department</Label>
                  <Select value={form.department} onValueChange={v => setForm({...form, department: v})}>
                    <SelectTrigger data-testid="form-department">
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Manager</Label>
                  <Input value={form.manager} onChange={e => setForm({...form, manager: e.target.value})} placeholder="John Smith" data-testid="form-manager" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Start Date</Label>
                  <Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} data-testid="form-start-date" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Office Location</Label>
                  <Input value={form.office_location} onChange={e => setForm({...form, office_location: e.target.value})} placeholder="Detroit" data-testid="form-location" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Cell Phone</Label>
                  <Input value={form.cell_phone} onChange={e => setForm({...form, cell_phone: e.target.value})} placeholder="555-123-4567" data-testid="form-cell-phone" />
                </div>
              </div>

              <div>
                <Label className="text-xs text-slate-500">Personal Email</Label>
                <Input value={form.personal_email} onChange={e => setForm({...form, personal_email: e.target.value})} placeholder="jane.doe@gmail.com" data-testid="form-personal-email" />
              </div>

              {/* Service Access */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-slate-700 mb-3">Service Access</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center justify-between p-2 rounded border">
                    <Label className="text-sm cursor-pointer">Business Central</Label>
                    <Switch checked={form.bc} onCheckedChange={v => setForm({...form, bc: v})} data-testid="form-bc" />
                  </div>
                  <div className="flex items-center justify-between p-2 rounded border">
                    <Label className="text-sm cursor-pointer">Teams Phone</Label>
                    <Switch checked={form.teams_phone} onCheckedChange={v => setForm({...form, teams_phone: v})} data-testid="form-teams-phone" />
                  </div>
                  <div className="flex items-center justify-between p-2 rounded border">
                    <Label className="text-sm cursor-pointer">Spiro CRM</Label>
                    <Switch checked={form.spiro} onCheckedChange={v => setForm({...form, spiro: v})} data-testid="form-spiro" />
                  </div>
                  <div className="flex items-center justify-between p-2 rounded border">
                    <Label className="text-sm cursor-pointer">JD Edwards</Label>
                    <Switch checked={form.jd_edwards} onCheckedChange={v => setForm({...form, jd_edwards: v})} data-testid="form-jd-edwards" />
                  </div>
                  <div className="flex items-center justify-between p-2 rounded border">
                    <Label className="text-sm cursor-pointer">Business Cards</Label>
                    <Switch checked={form.business_cards} onCheckedChange={v => setForm({...form, business_cards: v})} data-testid="form-biz-cards" />
                  </div>
                  <div className="flex items-center justify-between p-2 rounded border">
                    <Label className="text-sm cursor-pointer">Website Access</Label>
                    <Switch checked={form.website} onCheckedChange={v => setForm({...form, website: v})} data-testid="form-website" />
                  </div>
                </div>
              </div>

              {/* BC Role (shown when BC is enabled) */}
              {form.bc && (
                <div>
                  <Label className="text-xs text-slate-500">BC Role (optional — auto-detected from department if blank)</Label>
                  <Input value={form.bc_role} onChange={e => setForm({...form, bc_role: e.target.value})} placeholder="e.g. Sales, Finance, Quality" data-testid="form-bc-role" />
                </div>
              )}

              {/* Laptop */}
              <div>
                <Label className="text-xs text-slate-500">Preferred Laptop Model (optional)</Label>
                <Input value={form.laptop_model} onChange={e => setForm({...form, laptop_model: e.target.value})} placeholder="e.g. ThinkPad T14s" data-testid="form-laptop-model" />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t bg-slate-50 rounded-b-lg">
              <Button variant="outline" onClick={() => setShowForm(false)} data-testid="form-cancel-btn">Cancel</Button>
              <Button onClick={handleSubmitNewHire} disabled={submitting} data-testid="form-submit-btn">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <UserPlus className="h-4 w-4 mr-1" />}
                {submitting ? 'Processing...' : 'Start Onboarding'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
